/**
 * `BirthdayModal` — modal d'envoi manuel du mail d'anniversaire depuis
 * la carte Kanban (clic sur le 🎂).
 *
 * iter110n · CEO 2026-09-27 · WhatsApp
 *   Contexte : le scheduler automatique ne cible que les collaborateurs
 *   avec un contrat "signed" + actif. Or il arrive qu'un candidat pas
 *   encore signé (ex. Charlie ZERAOULIA, en "Contrat en signature") ait
 *   son anniversaire aujourd'hui. Ce modal permet à la RH de lui
 *   envoyer manuellement le mail Shariti, avec possibilité d'éditer le
 *   sujet + les paragraphes avant l'envoi.
 *
 * Idempotence : le backend rejette un 2ᵉ envoi le même jour (409). La
 * checkbox « Forcer un renvoi » utilise `force=true` pour bypasser ce
 * garde-fou dans les rares cas où c'est nécessaire.
 */
import { AlertTriangle, CheckCircle2, Eye, Loader2, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";


export default function BirthdayModal({
    open,
    onOpenChange,
    candidateId,
    permanentId,
    candidateName,
    onSent,
}) {
    // Determine which entity we're targeting (backwards compatible :
    // `candidateId` was the only prop initially — now we support PIs too).
    const entityType = permanentId ? "permanent" : "candidate";
    const entityId = permanentId || candidateId;
    const _basePath = entityType === "permanent"
        ? `/permanents/${entityId}`
        : `/candidates/${entityId}`;

    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [recipient, setRecipient] = useState(null);
    const [alreadySent, setAlreadySent] = useState(false);
    const [alreadySentAt, setAlreadySentAt] = useState(null);
    const [force, setForce] = useState(false);

    // Editable fields — undefined means "use backend default"
    const [subject, setSubject] = useState("");
    const [intro, setIntro] = useState("");
    const [body, setBody] = useState("");
    const [closing, setClosing] = useState("");
    const [signature, setSignature] = useState("");

    const [defaults, setDefaults] = useState(null);   // full default { subject, intro, body, closing, signature }
    const [previewHtml, setPreviewHtml] = useState("");

    // iter110n · CEO 2026-09-27 · Carrousel de templates pré-remplis.
    // La RH scrolle horizontalement → clique → les 5 champs éditables se
    // remplissent avec le contenu du template (placeholder {first} résolu
    // avec le prénom du destinataire). Ensuite libre à elle d'affiner.
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);

    const debounceRef = useRef(null);

    // Load templates catalog once on modal open (cheap: ~5 items, static)
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/birthday/templates");
                if (!cancelled) setTemplates(data.templates || []);
            } catch (_err) {
                // Non-critical — modal still works with empty fields (backend
                // falls back to default brand template)
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    // Interpolate {first} placeholder with the recipient's first name
    const _interpolate = (s) => (s || "").replaceAll("{first}", recipient?.first_name || "à toi");

    // Apply a template's content into the 5 editable fields
    const applyTemplate = (tpl) => {
        if (!tpl) return;
        setSelectedTemplateId(tpl.id);
        setSubject(_interpolate(tpl.subject));
        setIntro(_interpolate(tpl.intro_paragraph));
        setBody(_interpolate(tpl.body_paragraph));
        setClosing(_interpolate(tpl.closing_paragraph));
        setSignature(_interpolate(tpl.signature_line));
    };

    // Auto-apply the « default » template once both the recipient and the
    // catalog are loaded — so fields never appear empty. Only fires on
    // first open (`selectedTemplateId == null`) to avoid clobbering user
    // edits when the modal reopens.
    useEffect(() => {
        if (!open || !recipient || !templates.length || selectedTemplateId) return;
        const def = templates.find((t) => t.id === "default") || templates[0];
        if (def) applyTemplate(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, recipient, templates]);

    // Load preview + defaults on open
    useEffect(() => {
        if (!open || !entityId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`${_basePath}/birthday/preview`);
                if (cancelled) return;
                setRecipient(data.recipient);
                setAlreadySent(!!data.already_sent_today);
                setAlreadySentAt(data.already_sent_at || null);
                setPreviewHtml(data.html || "");
                setDefaults({ subject: data.subject || "" });
                setForce(false);
                // Reset editable fields — the templates carousel will
                // populate them via applyTemplate() once both `templates`
                // and `recipient` are available (see effect below).
                setSubject("");
                setIntro("");
                setBody("");
                setClosing("");
                setSignature("");
                setSelectedTemplateId(null);
            } catch (err) {
                toast.error(err.response?.data?.detail || "Impossible de charger l'aperçu");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, entityId, _basePath]);

    // Debounced preview refresh when user edits any field
    useEffect(() => {
        if (!open || !entityId || loading) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const params = {};
                if (subject) params.subject = subject;
                if (intro) params.intro_paragraph = intro;
                if (body) params.body_paragraph = body;
                if (closing) params.closing_paragraph = closing;
                if (signature) params.signature_line = signature;
                const { data } = await api.get(`${_basePath}/birthday/preview`, { params });
                setPreviewHtml(data.html || "");
            } catch (_err) {
                // Silent — preview refresh isn't critical
            }
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [subject, intro, body, closing, signature, open, entityId, loading, _basePath]);

    const handleSend = async () => {
        setSending(true);
        try {
            const payload = { force };
            if (subject && subject !== defaults?.subject) payload.subject = subject;
            if (intro) payload.intro_paragraph = intro;
            if (body) payload.body_paragraph = body;
            if (closing) payload.closing_paragraph = closing;
            if (signature) payload.signature_line = signature;

            const { data } = await api.post(`${_basePath}/birthday/send`, payload);
            toast.success(
                `🎉 Mail envoyé à ${data.recipient} !`,
                { description: data.receipt?.provider === "resend" ? "Livré via Resend." : "Enregistré (mode preview)." }
            );
            onSent?.(data);
            onOpenChange(false);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (typeof detail === "object" && detail?.error === "already_sent_today") {
                toast.error(detail.message, { duration: 6000 });
                setAlreadySent(true);
                setAlreadySentAt(detail.sent_at);
            } else {
                toast.error(typeof detail === "string" ? detail : "Erreur lors de l'envoi");
            }
        } finally {
            setSending(false);
        }
    };

    const sendDisabled = sending || loading || !recipient?.email || (alreadySent && !force);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl max-h-[92vh] overflow-y-auto p-0"
                data-testid="birthday-modal"
            >
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-orange-100 bg-gradient-to-br from-orange-50 to-white">
                    <DialogTitle className="flex items-center gap-2 text-xl">
                        <span className="text-2xl animate-bounce" style={{ animationDuration: "2.4s" }}>🎂</span>
                        <span>Envoyer un mail d'anniversaire</span>
                    </DialogTitle>
                    <DialogDescription className="mt-1">
                        {loading ? (
                            <span className="inline-flex items-center gap-2 text-slate-500">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…
                            </span>
                        ) : recipient ? (
                            <>
                                À <strong>{recipient.first_name} {recipient.last_name}</strong> ·{" "}
                                <span className="text-slate-600">{recipient.email}</span>
                                {recipient.birth_date && (
                                    <span className="ml-2 text-xs text-slate-500">
                                        (né·e le {recipient.birth_date})
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-slate-500">Candidat non chargé</span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {/* Already-sent warning */}
                {alreadySent && (
                    <div
                        className="mx-6 mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                        data-testid="birthday-modal-already-sent-alert"
                    >
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                        <div className="flex-1">
                            <div className="font-semibold">
                                Un mail d'anniversaire a déjà été envoyé aujourd'hui.
                            </div>
                            {alreadySentAt && (
                                <div className="text-xs mt-0.5 text-amber-800">
                                    Envoyé à {new Date(alreadySentAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}.
                                    Pour éviter un doublon, coche « Forcer un renvoi » si tu veux quand même relancer.
                                </div>
                            )}
                            <label className="mt-2 flex items-center gap-2 text-xs font-semibold cursor-pointer">
                                <Checkbox
                                    checked={force}
                                    onCheckedChange={(v) => setForce(!!v)}
                                    data-testid="birthday-modal-force-checkbox"
                                />
                                Forcer un renvoi (le collaborateur recevra un 2ᵉ mail)
                            </label>
                        </div>
                    </div>
                )}

                {/* iter110n · CEO 2026-09-27 · Carrousel de templates
                    pré-remplis. La RH scrolle horizontalement, clique sur
                    un template → les 5 champs du formulaire se remplissent
                    avec le contenu du template (placeholder {first} résolu
                    avec le prénom du destinataire). */}
                {templates.length > 0 && (
                    <div className="px-6 pt-4 pb-2">
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                Choisis un modèle de message
                            </div>
                            {selectedTemplateId && (
                                <div
                                    className="text-xs text-slate-500"
                                    data-testid="birthday-modal-template-description"
                                >
                                    {templates.find((t) => t.id === selectedTemplateId)?.description}
                                </div>
                            )}
                        </div>
                        <div
                            className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin"
                            data-testid="birthday-modal-template-carousel"
                            style={{ scrollbarWidth: "thin" }}
                        >
                            {templates.map((tpl) => {
                                const isSelected = tpl.id === selectedTemplateId;
                                return (
                                    <button
                                        key={tpl.id}
                                        type="button"
                                        onClick={() => applyTemplate(tpl)}
                                        data-testid={`birthday-modal-template-${tpl.id}`}
                                        className={`snap-start shrink-0 w-[168px] rounded-xl border-2 p-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                                            isSelected
                                                ? "border-orange-500 bg-gradient-to-br from-orange-50 to-white shadow-md scale-[1.02]"
                                                : "border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50/40 hover:-translate-y-0.5"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-2xl leading-none">{tpl.emoji}</span>
                                            {isSelected && (
                                                <CheckCircle2 className="w-4 h-4 text-orange-500" />
                                            )}
                                        </div>
                                        <div className={`mt-2 text-sm font-semibold leading-tight ${
                                            isSelected ? "text-orange-900" : "text-slate-800"
                                        }`}>
                                            {tpl.label}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-0 md:gap-4 px-6 py-4">
                    {/* -------- FORM -------- */}
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="birthday-subject" className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                Sujet du mail
                            </Label>
                            <Input
                                id="birthday-subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="🎉 Joyeux anniversaire …"
                                className="mt-1"
                                data-testid="birthday-modal-subject-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="birthday-intro" className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                Message d'introduction <span className="normal-case text-slate-400 font-normal">(sous-titre)</span>
                            </Label>
                            <Textarea
                                id="birthday-intro"
                                value={intro}
                                onChange={(e) => setIntro(e.target.value)}
                                placeholder="Laisse vide pour utiliser le message par défaut Shariti"
                                className="mt-1 min-h-[72px]"
                                data-testid="birthday-modal-intro-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="birthday-body" className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                Message principal
                            </Label>
                            <Textarea
                                id="birthday-body"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="Laisse vide pour le message par défaut"
                                className="mt-1 min-h-[84px]"
                                data-testid="birthday-modal-body-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="birthday-closing" className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                Message de clôture
                            </Label>
                            <Textarea
                                id="birthday-closing"
                                value={closing}
                                onChange={(e) => setClosing(e.target.value)}
                                placeholder="Laisse vide pour le message par défaut"
                                className="mt-1 min-h-[64px]"
                                data-testid="birthday-modal-closing-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="birthday-signature" className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                Signature ligne finale
                            </Label>
                            <Input
                                id="birthday-signature"
                                value={signature}
                                onChange={(e) => setSignature(e.target.value)}
                                placeholder={`Bon anniversaire ${recipient?.first_name || "à toi"}, on t'embrasse fort !`}
                                className="mt-1"
                                data-testid="birthday-modal-signature-input"
                            />
                        </div>

                        <p className="text-[11px] text-slate-500 italic leading-relaxed">
                            💡 Astuce : laisse un champ vide pour garder le message brandé Shariti par défaut.
                            Seul le sujet et les paragraphes modifiés seront personnalisés.
                        </p>
                    </div>

                    {/* -------- PREVIEW -------- */}
                    <div className="mt-4 md:mt-0">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                                <Eye className="w-3.5 h-3.5" /> Aperçu en direct
                            </Label>
                            <button
                                type="button"
                                onClick={() => setShowPreview((v) => !v)}
                                className="text-[11px] text-orange-600 hover:text-orange-700 hover:underline"
                                data-testid="birthday-modal-toggle-preview"
                            >
                                {showPreview ? "Masquer" : "Afficher"}
                            </button>
                        </div>
                        {showPreview && (
                            <iframe
                                title="Aperçu mail anniversaire"
                                srcDoc={previewHtml}
                                sandbox=""
                                className="w-full h-[520px] rounded-lg border border-slate-200 bg-white"
                                data-testid="birthday-modal-preview-iframe"
                            />
                        )}
                        {!showPreview && (
                            <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500">
                                Clique sur « Afficher » pour voir le rendu final du mail (avec confettis animés).
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="px-6 pb-6 pt-2 gap-2 flex-col-reverse sm:flex-row sm:justify-end">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={sending}
                        data-testid="birthday-modal-cancel-btn"
                    >
                        <X className="w-4 h-4 mr-1.5" /> Annuler
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={sendDisabled}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="birthday-modal-send-btn"
                    >
                        {sending ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Envoi…
                            </>
                        ) : alreadySent && force ? (
                            <>
                                <Send className="w-4 h-4 mr-1.5" /> Forcer un renvoi
                            </>
                        ) : alreadySent ? (
                            <>
                                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Déjà envoyé
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4 mr-1.5" /> Envoyer le mail
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
